import { CloseCircleFilled } from '@ant-design/icons'
import { useSessionTree } from '@services/session'
import { SelectProps } from '@shared/Form/Select'
import { Space } from 'antd'
import { isNil } from 'ramda'
import React from 'react'
import { useTranslation } from 'react-i18next'
import CitySelect from './CitySelect'
import SessionChildrenSelect from './SessionChildrenSelect'

type Props = Omit<SelectProps, 'onChange'> & {
  onChange?: (value: any) => void
  /**
   * 是否支援全選
   * default: true
   */
  supportAll?: boolean
  /**
   * 是否允許全部行政區
   * default: true
   */
  allowAllArea?: boolean
}

export const CityAreaSelect = (props: Props) => {
  const { t } = useTranslation()

  const {
    supportAll = true,
    allowAllArea = true,
    value,
    onChange,
    allowClear,
    ...others
  } = props

  const { byId, loading } = useSessionTree()

  const [cityId, setCityId] = React.useState<number | undefined>(undefined)
  const [areaId, setAreaId] = React.useState<number | undefined>(undefined)

  const _triggerChange = (value: any) => {
    onChange?.(value === -1 ? undefined : value)
  }

  const _handleCityChange = (value: any) => {
    setCityId(value)
    if (!allowAllArea) {
      const city = byId[value]
      if (city && city.children && city.children.length > 0) {
        setAreaId(city.children[0].id)
        _triggerChange(city.children[0].id)
      }
      return
    }
    setAreaId(supportAll ? -1 : undefined)
    // 可選擇全部時，不需強制選擇行政區
    _triggerChange(supportAll ? value : undefined)
  }

  const _handleAreaChange = (value: any) => {
    if (value === -1) {
      _triggerChange(cityId)
    } else {
      _triggerChange(value)
    }
  }

  React.useEffect(() => {
    const getRecursionDepth = (
      id: number | undefined,
      sesionLevel = 0
    ): number => {
      if (id === undefined || !byId[id]) {
        return sesionLevel
      }
      return getRecursionDepth(byId[id].parentSessionId, sesionLevel + 1)
    }

    if (value === undefined || value === -1) {
      return
    }
    const sesionLevel = getRecursionDepth(value as number)
    if (sesionLevel === 2) {
      setCityId(value as number)
    }
    if (sesionLevel === 3) {
      setCityId(byId[value as number].parentSessionId)
      setAreaId(value as number)
    }
  }, [value])

  return (
    <Space>
      <CitySelect
        loading={loading}
        supportAll={supportAll}
        onChange={_handleCityChange}
        value={cityId}
        allowClear={false}
        {...others}
      />
      <SessionChildrenSelect
        loading={loading}
        notFoundContent="請先選擇縣市"
        supportAll={supportAll || allowAllArea}
        parentId={cityId}
        onChange={_handleAreaChange}
        value={areaId}
        allowClear={false}
        {...others}
      />
      {allowClear && (!isNil(cityId) || !isNil(areaId)) && (
        <CloseCircleFilled
          style={{ color: '#fff' }}
          onClick={() => {
            setCityId(undefined)
            setAreaId(undefined)
            _triggerChange(undefined)
          }}
        />
      )}
    </Space>
  )
}

export default CityAreaSelect
